import "../../css/FullEvent/FullEvent.css";
import "../../css/GlobalStyles.css";
import { memo, useCallback, useMemo, useState } from "react";
import {
  Event,
  CustomTicketV2,
  TicketType,
  PromoCode,
  TicketV2,
} from "@markit/common.types";
import { FormatCurrency } from "../../utils/FormatCurrency";
import {
  getTicketLabels,
  getTicketsRemainingInGroup,
} from "../../utils/eventUtils/eventUtils";
import { isDesktop } from "react-device-detect";
import { useTheme } from "../../hooks/useTheme";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import { Icon } from "@iconify/react";
import { SelectCircleRow } from "../FullEventSubComponents/SelectCircleRow";
import BuyBanner from "../FullEventSubComponents/BuyBanner";
import FullEventPriceDisplay from "./FullEventPriceDisplay";
import { isEventRelatedType } from "@markit/common.utils";

type FullEventInfoTicketsProps = {
  event: Event;
  visibleTickets: CustomTicketV2[];
  selectedCustomTickets: CustomTicketV2[];
  setSelectedCustomTickets: (selectedCustomTickets: CustomTicketV2[]) => void;
  promoCode: PromoCode | undefined;
  redeemTicket: TicketV2 | undefined;
  setShowCheckoutForm: (showCheckoutForm: boolean) => void;
};

// Displays the ticket options on the top level event/product page
const FullEventInfoTickets = memo(function FullEventInfoTicketsFn(
  props: FullEventInfoTicketsProps
) {
  const {
    event,
    visibleTickets,
    selectedCustomTickets,
    setSelectedCustomTickets,
    promoCode,
    redeemTicket,
    setShowCheckoutForm,
  } = props;
  const [labels, setLabels] = useState<string[][]>([[]]);
  const [groupTicketsRemaining, setGroupTicketsRemaining] = useState<number[]>(
    []
  );
  const [loadingTickets, setLoadingTickets] = useState(true);

  const { theme } = useTheme();

  useAsyncOnMount(async () => {
    const ticketsRemainingInGroups = await Promise.all(
      visibleTickets.map(
        async (customTicket: CustomTicketV2) =>
          await getTicketsRemainingInGroup(customTicket, event)
      )
    );
    setGroupTicketsRemaining(ticketsRemainingInGroups);

    const allLabels = await Promise.all(
      visibleTickets.map(
        async (customTicket: CustomTicketV2, index: number) =>
          await getTicketLabels(
            customTicket,
            ticketsRemainingInGroups[index],
            event.eventType
          )
      )
    );
    setLabels(allLabels);

    // The first available ticket is the first ticket found with tickets remaining (with quantity) or quantity not set (no quantity limit)
    const firstAvailableTicket =
      visibleTickets[
        Math.max(
          ticketsRemainingInGroups.findIndex(
            (numTicketsRemaining) =>
              numTicketsRemaining === -1 || numTicketsRemaining > 0
          ),
          0
        )
      ];
    setSelectedCustomTickets(
      firstAvailableTicket ? [firstAvailableTicket] : []
    );
    setLoadingTickets(false);
  });

  const allOutOfStock = useMemo(() => {
    return groupTicketsRemaining.every(
      (numTicketsRemaining) => numTicketsRemaining === 0
    );
  }, [groupTicketsRemaining]);

  const isEventOver = useMemo(
    () =>
      isEventRelatedType(event.eventType) && new Date(event.end) < new Date(),
    [event.end, event.eventType]
  );

  const hasFlexiblePricingTickets = useMemo(
    () => visibleTickets.some((ticket) => ticket.flexiblePricing),
    [visibleTickets]
  );

  const ticketPriceLabel = useCallback(
    (customTicket: CustomTicketV2) =>
      customTicket.type === TicketType.FREE
        ? "Free"
        : customTicket.type === TicketType.REQUEST
        ? "Request"
        : FormatCurrency(customTicket.price / 100) +
          (customTicket.flexiblePricing ? "±" : ""),
    []
  );

  const soldOutCustomTicket = useCallback(
    (index: number) => {
      return (
        !loadingTickets &&
        ((groupTicketsRemaining !== null && !groupTicketsRemaining[index]) ||
          event.isManualSoldOut)
      );
    },
    [groupTicketsRemaining, event.isManualSoldOut, loadingTickets]
  );

  // Single-select only for top level
  const itemOnPress = useCallback(
    (customTicket: CustomTicketV2) => {
      setSelectedCustomTickets([customTicket]);
    },
    [setSelectedCustomTickets]
  );

  return (
    <div
      className="ColumnNormal"
      style={{
        ...theme.NewPrimaryBG,
        gap: 14,
        padding: 14,
        borderRadius: 20,
        border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
      }}
    >
      {isEventRelatedType(event.eventType) &&
      visibleTickets.some((event) => event.type === TicketType.PAID) ? (
        <span
          className="bodyMedium"
          style={{ fontWeight: 600, ...theme.PrimaryText }}
        >
          Tickets
        </span>
      ) : null}
      <div className="ColumnNormal" style={{ gap: 12 }}>
        {visibleTickets.map((customTicket, index) => {
          const isSelected = selectedCustomTickets.some(
            (ticket) => ticket.id === customTicket.id
          );
          return (
            <SelectCircleRow
              key={index}
              event={event}
              customTicket={customTicket}
              isDisabled={soldOutCustomTicket(index) || isEventOver}
              isSoldOut={soldOutCustomTicket(index)}
              onPress={() => itemOnPress(customTicket)}
              priceTag={
                isDesktop ? (
                  <FullEventPriceDisplay
                    eventType={event.eventType}
                    ticketQuantity={1}
                    customTicket={customTicket}
                    selectedCustomTicket={
                      isSelected
                        ? customTicket
                        : selectedCustomTickets[0] ?? customTicket
                    }
                    promoCode={promoCode}
                    isTixId={!!redeemTicket}
                    isSoldOut={soldOutCustomTicket(index)}
                    theme={theme}
                  />
                ) : undefined
              }
              multiTicketText={null}
              description={
                labels[index] &&
                labels[index].length > 0 &&
                labels.length === visibleTickets.length
                  ? labels[index].join(" • ")
                  : ""
              }
              isSelected={isSelected}
              rightIcon={<></>}
              isCheckout={false}
              outerRightIcon={
                soldOutCustomTicket(index) ? (
                  <></>
                ) : (
                  <div className="AlignedRow" style={{ gap: 7 }}>
                    {!isDesktop ? (
                      <div
                        className="Centering sectionTitle"
                        style={{
                          borderRadius: 12,
                          border: `1px solid ${theme.TextFieldBorder.borderColor}`,
                          backgroundColor: theme.NewPrimaryBG.backgroundColor,
                          padding: "6px 8px",
                          color: theme.PrimaryText.color,
                        }}
                      >
                        <span className="bodyMedium">
                          {ticketPriceLabel(customTicket)}
                        </span>
                      </div>
                    ) : null}
                    {!isEventOver && isSelected ? (
                      <Icon
                        icon="ion:checkmark-circle"
                        height={25}
                        style={{ color: theme.PrimaryText.color }}
                      />
                    ) : null}
                  </div>
                )
              }
            />
          );
        })}
      </div>
      <div className="ColumnNormal" style={{ gap: 10 }}>
        <BuyBanner
          event={event}
          ticketData={redeemTicket}
          promoCode={promoCode}
          isEventAtCapacity={allOutOfStock && !loadingTickets}
          loadingState={loadingTickets}
          setShowCheckoutForm={setShowCheckoutForm}
          visibleTickets={visibleTickets}
        />
        {hasFlexiblePricingTickets ? (
          <div className="AlignedRow" style={{ gap: 4 }}>
            <span className="bodySubtext">±</span>
            <span className="smallBodySubtext">
              Suggested price shown. Name your own price at checkout.
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default FullEventInfoTickets;
